import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getBackendUrl } from '../../lib/utils';
import { 
  Save, RefreshCw, Eye, Globe, FileText, AlertCircle, CheckCircle2, 
  ChevronRight, ExternalLink, HelpCircle, Code, Settings, ListCollapse,
  UploadCloud, Trash2, Image as ImageIcon
} from 'lucide-react';

const AdminSEO = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // SEO General Settings State
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDesc, setSeoDesc] = useState('');
  const [seoKeywords, setSeoKeywords] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');

  // robots.txt & llms.txt State
  const [robotsTxt, setRobotsTxt] = useState('');
  const [llmsTxt, setLlmsTxt] = useState('');

  // Sitemap state
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [sitemapUrls, setSitemapUrls] = useState([]);
  const [sitemapLoading, setSitemapLoading] = useState(false);

  // Logo URL for snippet preview
  const [logoUrl, setLogoUrl] = useState('');

  useEffect(() => {
    fetchSEOSettings();
  }, []);

  const fetchSEOSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.get(`${backendUrl}/api/settings`);
      
      // Map loaded settings
      if (res.data.seo_settings) {
        setSeoTitle(res.data.seo_settings.title || '');
        setSeoDesc(res.data.seo_settings.description || '');
        setSeoKeywords(res.data.seo_settings.keywords || '');
        setFaviconUrl(res.data.seo_settings.favicon_url || '');
      }
      
      if (res.data.robots_txt) {
        setRobotsTxt(res.data.robots_txt);
      }
      
      if (res.data.llms_txt) {
        setLlmsTxt(res.data.llms_txt);
      }

      if (res.data.footer_settings && res.data.footer_settings.logo_url) {
        setLogoUrl(res.data.footer_settings.logo_url);
      }
    } catch (err) {
      console.error("SEO settings load error", err);
      setError("SEO ayarları yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneralSEO = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.post(`${backendUrl}/api/seo/settings`, {
        title: seoTitle,
        description: seoDesc,
        keywords: seoKeywords,
        favicon_url: faviconUrl
      }, { withCredentials: true });
      setSuccess(res.data.message || "Genel SEO ayarları başarıyla güncellendi.");
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error("Save general SEO error", err);
      setError(err.response?.data?.detail || "SEO ayarları kaydedilemedi.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRobotsTxt = async () => {
    setLoading(true);
    setSuccess('');
    setError('');
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.post(`${backendUrl}/api/seo/robots`, {
        content: robotsTxt
      }, { withCredentials: true });
      setSuccess(res.data.message || "robots.txt dosyası başarıyla kaydedildi ve güncellendi.");
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error("Save robots.txt error", err);
      setError(err.response?.data?.detail || "robots.txt kaydedilemedi.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLlmsTxt = async () => {
    setLoading(true);
    setSuccess('');
    setError('');
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.post(`${backendUrl}/api/seo/llms`, {
        content: llmsTxt
      }, { withCredentials: true });
      setSuccess(res.data.message || "llms.txt dosyası başarıyla kaydedildi ve güncellendi.");
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error("Save llms.txt error", err);
      setError(err.response?.data?.detail || "llms.txt kaydedilemedi.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSitemap = async () => {
    setSitemapLoading(true);
    setSuccess('');
    setError('');
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.post(`${backendUrl}/api/seo/sitemap-generate`, {}, { withCredentials: true });
      setSitemapUrl(res.data.sitemap_url);
      setSitemapUrls(res.data.urls || []);
      setSuccess(res.data.message || "XML Site Haritası başarıyla oluşturuldu.");
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error("Sitemap generation error", err);
      setError(err.response?.data?.detail || "Site haritası oluşturulamadı.");
    } finally {
      setSitemapLoading(false);
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    setLoading(true);
    setSuccess('');
    setError('');
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.post(`${backendUrl}/api/settings/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      setFaviconUrl(res.data.image_url);
      setSuccess("Favicon başarıyla yüklendi. Değişiklikleri kalıcı yapmak için sayfa altındaki kaydet butonuna basmayı unutmayın!");
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error("Favicon upload error", err);
      setError("Favicon yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  // Pre-load sitemap URLs list in background if activeTab is sitemap
  useEffect(() => {
    if (activeTab === 'sitemap' && sitemapUrls.length === 0) {
      // Trigger a silent compile so we populate the table
      const loadSitemapUrls = async () => {
        try {
          const backendUrl = getBackendUrl();
          const res = await axios.post(`${backendUrl}/api/seo/sitemap-generate`, {}, { withCredentials: true });
          setSitemapUrl(res.data.sitemap_url);
          setSitemapUrls(res.data.urls || []);
        } catch (e) {
          console.error("Silent sitemap preload failed", e);
        }
      };
      loadSitemapUrls();
    }
  }, [activeTab]);

  // Character Limit Helpers
  const titleIdealMin = 40;
  const titleIdealMax = 60;
  const descIdealMin = 120;
  const descIdealMax = 160;

  const getLengthBadgeColor = (len, min, max) => {
    if (len === 0) return 'bg-slate-100 text-slate-500';
    if (len >= min && len <= max) return 'bg-emerald-50 text-emerald-600 border border-emerald-200';
    if (len < min) return 'bg-amber-50 text-amber-600 border border-amber-200';
    return 'bg-rose-50 text-rose-600 border border-rose-200';
  };

  const getLengthText = (len, min, max) => {
    if (len === 0) return 'Doldurulmadı';
    if (len >= min && len <= max) return 'Harika uzunluk';
    if (len < min) return 'Çok kısa';
    return 'Çok uzun (Google sonuçlarında kesilebilir)';
  };

  return (
    <div className="space-y-6">
      {/* Toast Alert Notifications */}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs font-bold text-emerald-700 flex items-center gap-2 animate-fade-in shadow-sm shadow-emerald-50/50">
          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-xs font-bold text-rose-700 flex items-center gap-2 animate-fade-in shadow-sm shadow-rose-50/50">
          <AlertCircle size={16} className="text-rose-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Main Container */}
      <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm shadow-slate-100 overflow-hidden">
        {/* Navigation Tabs Header */}
        <div className="flex border-b border-slate-100 bg-slate-50/50 p-2 gap-1">
          <button
            onClick={() => setActiveTab('general')}
            className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-xs font-bold tracking-tight transition-all duration-200 ${
              activeTab === 'general'
                ? 'bg-white text-blue-600 shadow-sm shadow-slate-200/50 border border-slate-200/40'
                : 'text-slate-500 hover:text-slate-800 hover:bg-white/40'
            }`}
          >
            <Globe size={15} />
            Genel Arama Görünümü (Meta)
          </button>
          <button
            onClick={() => setActiveTab('files')}
            className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-xs font-bold tracking-tight transition-all duration-200 ${
              activeTab === 'files'
                ? 'bg-white text-blue-600 shadow-sm shadow-slate-200/50 border border-slate-200/40'
                : 'text-slate-500 hover:text-slate-800 hover:bg-white/40'
            }`}
          >
            <Code size={15} />
            robots.txt & llms.txt Editörü
          </button>
          <button
            onClick={() => setActiveTab('sitemap')}
            className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-xs font-bold tracking-tight transition-all duration-200 ${
              activeTab === 'sitemap'
                ? 'bg-white text-blue-600 shadow-sm shadow-slate-200/50 border border-slate-200/40'
                : 'text-slate-500 hover:text-slate-800 hover:bg-white/40'
            }`}
          >
            <Settings size={15} />
            XML Site Haritası (Sitemap)
          </button>
        </div>

        {/* Tab Contents */}
        <div className="p-8">
          
          {/* TAB 1: GENERAL SEO (Meta Title, Description, Keywords & Google Search Preview) */}
          {activeTab === 'general' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              
              {/* Form Controls (Left / 7 cols) */}
              <form onSubmit={handleSaveGeneralSEO} className="lg:col-span-7 space-y-5">
                <div>
                  <h3 className="text-sm font-extrabold text-slate-800 mb-1">Ana Sayfa SEO Ayarları</h3>
                  <p className="text-[11px] text-slate-500 mb-4">Arama motorlarının ana sayfanızı nasıl listeleyeceğini yapılandırın. Yoast SEO kriterlerine göre doldurmanız tavsiye edilir.</p>
                </div>

                {/* Meta Title Input */}
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      Başlık (Meta Title)
                      <HelpCircle size={13} className="text-slate-400 hover:text-slate-600 cursor-pointer" title="Arama sonuçlarında mavi başlık olarak gösterilen metin." />
                    </label>
                    <span className={`text-[10px] px-2 py-0.5 rounded font-extrabold ${getLengthBadgeColor(seoTitle.length, titleIdealMin, titleIdealMax)}`}>
                      {seoTitle.length} Karakter - {getLengthText(seoTitle.length, titleIdealMin, titleIdealMax)}
                    </span>
                  </div>
                  <input
                    type="text"
                    required
                    value={seoTitle}
                    onChange={(e) => setSeoTitle(e.target.value)}
                    placeholder="Örn: İSİLKOOP | Modern Üretim ve Ticaret Alanları Kooperatifi"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                  />
                  <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${
                        seoTitle.length >= titleIdealMin && seoTitle.length <= titleIdealMax ? 'bg-emerald-500' : 'bg-amber-500'
                      }`}
                      style={{ width: `${Math.min(100, (seoTitle.length / titleIdealMax) * 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Meta Description Input */}
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      Açıklama (Meta Description)
                      <HelpCircle size={13} className="text-slate-400 hover:text-slate-600 cursor-pointer" title="Arama motorlarında başlığın altında çıkan özet açıklama." />
                    </label>
                    <span className={`text-[10px] px-2 py-0.5 rounded font-extrabold ${getLengthBadgeColor(seoDesc.length, descIdealMin, descIdealMax)}`}>
                      {seoDesc.length} Karakter - {getLengthText(seoDesc.length, descIdealMin, descIdealMax)}
                    </span>
                  </div>
                  <textarea
                    rows={4}
                    required
                    value={seoDesc}
                    onChange={(e) => setSeoDesc(e.target.value)}
                    placeholder="Örn: S.S. İkitelli - Silivri Toplu İşyeri Yapı Kooperatifi. Bölgede iş dünyasının ihtiyaçlarına yönelik modern, güvenilir ve sürdürülebilir üretim-ticaret alanları inşa ediyoruz."
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors resize-none"
                  />
                  <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${
                        seoDesc.length >= descIdealMin && seoDesc.length <= descIdealMax ? 'bg-emerald-500' : 'bg-amber-500'
                      }`}
                      style={{ width: `${Math.min(100, (seoDesc.length / descIdealMax) * 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Meta Keywords Input */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    Anahtar Kelimeler (Meta Keywords)
                    <span className="text-[10px] text-slate-400 font-normal">(Virgülle ayırarak giriniz)</span>
                  </label>
                  <input
                    type="text"
                    value={seoKeywords}
                    onChange={(e) => setSeoKeywords(e.target.value)}
                    placeholder="Örn: silivri kooperatif, sanayi sitesi, işyeri, ikitelli kooperatif, isilkoop"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                  />
                </div>

                {/* Favicon Upload Section */}
                <div className="bg-slate-50 border border-slate-200/60 p-5 rounded-2xl space-y-4">
                  <div className="space-y-0.5">
                    <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <ImageIcon size={14} className="text-blue-500" />
                      Site Favicon (Sekme İkonu)
                    </h4>
                    <p className="text-[10px] text-slate-500">Google aramalarında ve tarayıcı sekmelerinde görünür. Kare (1:1) oranında, tercihen 512x512 veya 192x192 PNG/ICO formatında yükleyin.</p>
                  </div>

                  <div className="flex flex-wrap items-center gap-6">
                    {/* Upload button */}
                    <label className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold cursor-pointer transition shadow-md shadow-blue-600/10 shrink-0">
                      <UploadCloud size={14} /> Yeni Favicon Yükle
                      <input type="file" accept="image/*,.ico" className="hidden" onChange={handleFaviconUpload} />
                    </label>

                    {/* Previews in different sizes */}
                    {faviconUrl && (
                      <div className="flex items-center gap-4 border border-slate-200/60 p-3 bg-white rounded-xl">
                        {/* 32x32 */}
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-8 h-8 rounded border border-slate-200 flex items-center justify-center p-0.5 bg-slate-50">
                            <img src={`${getBackendUrl()}${faviconUrl}`} alt="32x32" className="w-full h-full object-contain" />
                          </div>
                          <span className="text-[9px] font-bold text-slate-400">32x32</span>
                        </div>
                        {/* 64x64 */}
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-12 h-12 rounded border border-slate-200 flex items-center justify-center p-1 bg-slate-50">
                            <img src={`${getBackendUrl()}${faviconUrl}`} alt="64x64" className="w-full h-full object-contain" />
                          </div>
                          <span className="text-[9px] font-bold text-slate-400">64x64</span>
                        </div>
                        {/* 128x128 / High-res preview */}
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-16 h-16 rounded border border-slate-200 flex items-center justify-center p-1.5 bg-slate-50">
                            <img src={`${getBackendUrl()}${faviconUrl}`} alt="128x128" className="w-full h-full object-contain" />
                          </div>
                          <span className="text-[9px] font-bold text-slate-400">512x512 (HQ)</span>
                        </div>

                        {/* Reset button */}
                        <button
                          type="button"
                          onClick={() => setFaviconUrl('')}
                          className="p-2 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-xl transition ml-2"
                          title="Favicon'u Kaldır"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-2 text-right">
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-600/10 disabled:opacity-50"
                  >
                    <Save size={16} />
                    {loading ? "Kaydediliyor..." : "SEO Bilgilerini Kaydet"}
                  </button>
                </div>
              </form>

              {/* SERP Search Preview Widget (Right / 5 cols) */}
              <div className="lg:col-span-5 space-y-6 lg:border-l lg:border-slate-100 lg:pl-8">
                <div>
                  <h3 className="text-sm font-extrabold text-slate-800 mb-1 flex items-center gap-1.5">
                    <Eye size={16} className="text-slate-500" />
                    Google Arama Sonuç Önizlemesi
                  </h3>
                  <p className="text-[11px] text-slate-500">Masaüstü ve mobil aramalarda sayfanızın Google üzerinde nasıl görüneceğini gerçek zamanlı simüle edin.</p>
                </div>

                {/* Google SERP Snippet Preview Box */}
                <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm space-y-4 max-w-md">
                  {/* Google Header bar */}
                  <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                    <div className="w-3 h-3 rounded-full bg-rose-400"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                    <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                    <span className="text-[10px] text-slate-400 font-extrabold ml-1 uppercase tracking-wider">google.com.tr</span>
                  </div>

                  {/* Desktop Preview */}
                  <div className="space-y-1 bg-white">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      {/* Logo favicon simulator */}
                      <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200 overflow-hidden shrink-0">
                        {faviconUrl ? (
                          <img src={`${getBackendUrl()}${faviconUrl}`} alt="Favicon" className="w-3.5 h-3.5 object-contain" />
                        ) : logoUrl ? (
                          <img src={`${getBackendUrl()}${logoUrl}`} alt="Favicon" className="w-3.5 h-3.5 object-contain" />
                        ) : (
                          <Globe size={12} className="text-slate-400" />
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-slate-900 font-medium text-[11px] leading-tight">S.S. İSİLKOOP</span>
                        <span className="text-[10px] text-slate-500 leading-tight">https://isilkoop.com</span>
                      </div>
                    </div>

                    {/* Meta Title Link */}
                    <h4 className="text-xl text-[#1a0dab] hover:underline cursor-pointer font-medium leading-snug pt-1 truncate-2-lines">
                      {seoTitle || "Lütfen bir başlık giriniz..."}
                    </h4>

                    {/* Meta Description Snippet */}
                    <p className="text-xs text-[#4d5156] leading-relaxed pt-0.5 break-words">
                      {seoDesc || "Google aramalarında web sitenizin açıklaması burada görüntülenecektir. Lütfen sol taraftaki formdan açıklama ekleyin."}
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 text-[11px] text-slate-600 space-y-1.5">
                  <span className="font-bold text-blue-700 block">💡 SEO Önerileri:</span>
                  <p>• Başlığınızın ilk kelimesine anahtar kelimenizi yerleştirmek sıralamayı olumlu etkiler.</p>
                  <p>• Meta açıklamalarında kullanıcıyı harekete geçirici (Call-to-Action) ifadeler kullanın.</p>
                  <p>• Anahtar kelimelerinizi sitenizin genel içeriğiyle uyumlu seçin.</p>
                </div>

              </div>

            </div>
          )}

          {/* TAB 2: ROBOTS.TXT & LLMS.TXT EDITORS */}
          {activeTab === 'files' && (
            <div className="space-y-8">
              
              {/* Header */}
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-sm font-extrabold text-slate-800 mb-1">Arama Motoru & LLM Bot Ayarları</h3>
                <p className="text-[11px] text-slate-500">
                  Bot yönlendirme dosyalarını düzenleyin. Kaydet butonu hem veritabanını günceller hem de doğrudan kök dizindeki 
                  <strong className="text-slate-700"> robots.txt</strong> ve <strong className="text-slate-700">llms.txt</strong> dosyalarını fiziksel olarak yazar.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* Robots.txt Editor */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="space-y-0.5">
                      <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                        <FileText size={14} className="text-blue-500" />
                        robots.txt
                      </h4>
                      <p className="text-[10px] text-slate-500">Google, Yandex gibi arama motoru botlarının kuralları.</p>
                    </div>
                    <a 
                      href={`${getBackendUrl()}/robots.txt`} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="text-[10px] font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 bg-slate-50 hover:bg-slate-100 px-2.5 py-1.5 rounded-lg border border-slate-200 transition"
                    >
                      Canlı Gör <ExternalLink size={10} />
                    </a>
                  </div>

                  <div className="relative">
                    <textarea
                      rows={12}
                      value={robotsTxt}
                      onChange={(e) => setRobotsTxt(e.target.value)}
                      className="w-full font-mono text-xs bg-slate-900 text-slate-200 p-5 rounded-2xl border border-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/30 leading-relaxed"
                      placeholder="User-agent: *&#10;Allow: /"
                    />
                  </div>

                  <div className="bg-slate-50 p-3.5 rounded-xl text-[10px] text-slate-500 flex flex-col gap-1">
                    <span>• <strong className="text-slate-700">{'{site_url}'}</strong> ifadesi, kaydedilirken otomatik olarak mevcut sunucu URL'inizle değiştirilir.</span>
                    <span>• Sitemap bağlantısını mutlaka en alta ekleyin (örn: Sitemap: {'{site_url}'}sitemap.xml).</span>
                  </div>

                  <button
                    onClick={handleSaveRobotsTxt}
                    disabled={loading}
                    className="w-full bg-slate-800 hover:bg-slate-900 text-white py-3 rounded-xl text-xs font-bold transition-all shadow-md shadow-slate-800/10 flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    <Save size={14} />
                    robots.txt Kaydet & Yayınla
                  </button>
                </div>

                {/* LLMs.txt Editor */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="space-y-0.5">
                      <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                        <Code size={14} className="text-amber-500" />
                        llms.txt (LLM Txt)
                      </h4>
                      <p className="text-[10px] text-slate-500">ChatGPT, Claude vb. yapay zeka modelleri için sitenin markdown özeti.</p>
                    </div>
                    <a 
                      href={`${getBackendUrl()}/llms.txt`} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="text-[10px] font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 bg-slate-50 hover:bg-slate-100 px-2.5 py-1.5 rounded-lg border border-slate-200 transition"
                    >
                      Canlı Gör <ExternalLink size={10} />
                    </a>
                  </div>

                  <div className="relative">
                    <textarea
                      rows={12}
                      value={llmsTxt}
                      onChange={(e) => setLlmsTxt(e.target.value)}
                      className="w-full font-mono text-xs bg-slate-900 text-slate-200 p-5 rounded-2xl border border-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/30 leading-relaxed"
                      placeholder="# S.S. İSİLKOOP"
                    />
                  </div>

                  <div className="bg-slate-50 p-3.5 rounded-xl text-[10px] text-slate-500 flex flex-col gap-1">
                    <span>• Bu dosya, yapay zekaların sitenizi daha doğru anlamasını ve özetlemesini sağlar.</span>
                    <span>• Markdown (MD) formatında kurumsal hizmetler, link listeleri ve özet sunumlar hazırlayın.</span>
                  </div>

                  <button
                    onClick={handleSaveLlmsTxt}
                    disabled={loading}
                    className="w-full bg-slate-800 hover:bg-slate-900 text-white py-3 rounded-xl text-xs font-bold transition-all shadow-md shadow-slate-800/10 flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    <Save size={14} />
                    llms.txt Kaydet & Yayınla
                  </button>
                </div>

              </div>

            </div>
          )}

          {/* TAB 3: XML SITE MAP (SITEMAP.XML) */}
          {activeTab === 'sitemap' && (
            <div className="space-y-6">
              
              {/* Top Panel card */}
              <div className="bg-slate-50 border border-slate-200/60 p-6 rounded-3xl grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                <div className="md:col-span-2 space-y-1">
                  <h3 className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
                    <Settings size={18} className="text-blue-600" />
                    XML Site Haritası Durumu
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Sistemdeki tüm statik sayfaları, güncel haberleri ve dinamik CMS sayfalarını derleyerek bir site haritası oluşturur.
                  </p>
                  
                  {sitemapUrl && (
                    <div className="pt-2 flex items-center gap-2">
                      <span className="text-[10px] font-bold text-slate-400 uppercase">Site Haritası URL:</span>
                      <a 
                        href={sitemapUrl} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="text-xs font-bold text-blue-600 hover:underline inline-flex items-center gap-1"
                      >
                        {sitemapUrl}
                        <ExternalLink size={11} />
                      </a>
                    </div>
                  )}
                </div>

                <div className="text-right">
                  <button
                    onClick={handleGenerateSitemap}
                    disabled={sitemapLoading}
                    className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3.5 rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-600/10 disabled:opacity-50"
                  >
                    <RefreshCw size={14} className={sitemapLoading ? 'animate-spin' : ''} />
                    {sitemapLoading ? "Harita Güncelleniyor..." : "Site Haritasını Güncelle"}
                  </button>
                </div>
              </div>

              {/* Table section */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                    <ListCollapse size={14} className="text-slate-500" />
                    Site Haritasına Eklenen URL Adresleri ({sitemapUrls.length})
                  </h4>
                  <span className="text-[10px] text-slate-400 font-medium">Bu sayfalar otomatik taranarak sitemap.xml içerisine eklenmiştir.</span>
                </div>

                <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 font-bold border-b border-slate-100">
                          <th className="p-4">URL Adresi</th>
                          <th className="p-4 w-32 text-center">Değişim Sıklığı</th>
                          <th className="p-4 w-24 text-center">Öncelik</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {sitemapUrls.length > 0 ? (
                          sitemapUrls.map((u, i) => (
                            <tr key={i} className="hover:bg-slate-50/50 transition font-medium text-slate-600">
                              <td className="p-4">
                                <a 
                                  href={u.loc} 
                                  target="_blank" 
                                  rel="noreferrer" 
                                  className="text-blue-600 hover:underline break-all"
                                >
                                  {u.loc}
                                </a>
                              </td>
                              <td className="p-4 text-center capitalize text-slate-500">
                                <span className="bg-slate-100 px-2.5 py-1 rounded-full text-[10px] font-bold">
                                  {u.changefreq}
                                </span>
                              </td>
                              <td className="p-4 text-center font-bold text-slate-700">
                                {u.priority}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={3} className="p-8 text-center text-slate-400 font-medium">
                              {sitemapLoading ? "Site haritası taranıyor..." : "Site haritası henüz oluşturulmadı. Yukarıdaki butona tıklayarak taramayı başlatın."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AdminSEO;
